import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { Package, Star, CheckCircle, Sparkles, Plus, Trash2 } from 'lucide-react';

export default function DeliveryApprovalPage() {
  const { token } = useParams<{ token: string }>();
  const [approved, setApproved] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [name, setName] = useState('');
  const [punchList, setPunchList] = useState<string[]>([]);
  const [punchInput, setPunchInput] = useState('');

  const handleApprove = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || rating === 0) return;
    setApproving(true);
    await new Promise(r => setTimeout(r, 800));
    setApproving(false);
    setApproved(true);
  };

  if (approved) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: '#080909' }} data-theme="dark">
        <div className="rounded-xl p-8 text-center max-w-sm w-full animate-scale-in card-shadow" style={{ background: 'rgba(20,21,21,0.92)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border" style={{ background: 'var(--success-bg)', borderColor: 'var(--success-border)' }}>
            <CheckCircle size={28} style={{ color: 'var(--success-dot)' }} />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Delivery Approved</h2>
          <p className="text-sm mt-2" style={{ color: 'var(--text-tertiary)' }}>Thank you for your feedback, {name}.</p>
          <div className="flex items-center justify-center gap-1 mt-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={16} style={{ color: i < rating ? 'var(--warning-dot)' : 'var(--text-muted)' }} className={i < rating ? 'fill-amber-400' : ''} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: '#080909' }} data-theme="dark">
      <div className="max-w-lg mx-auto">
        {/* Brand mark */}
        <div className="flex flex-col items-center mb-6">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(145deg, #10b981, #059669)',
              boxShadow: '0 0 0 1px rgba(16,185,129,0.3), 0 8px 24px rgba(16,185,129,0.22)',
            }}
          >
            <Sparkles size={22} className="text-white" strokeWidth={1.5} />
          </div>
          <p className="text-xs mt-2 text-center" style={{ color: 'rgba(255,255,255,0.28)' }}>
            converze &middot; Questions? Contact your advisor
          </p>
        </div>

        <div className="rounded-xl p-6 space-y-6 card-shadow" style={{ background: 'rgba(20,21,21,0.92)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
              <Package size={22} style={{ color: 'rgba(255,255,255,0.28)' }} />
            </div>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Delivery Approval</h1>
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Please review and approve the completed work</p>
          </div>

          <div className="rounded-lg p-4" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
            <h2 className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Project Summary</h2>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Bathroom renovation completed</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>All work items have been delivered according to the signed quote.</p>
          </div>

          <form onSubmit={handleApprove} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Your Full Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                className="w-full px-3 py-2.5 text-sm rounded-lg transition-all focus:outline-none focus:ring-1"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' }}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Rating</label>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setRating(i + 1)}
                    className="p-1"
                  >
                    <Star size={24} style={{ color: i < rating ? 'var(--warning-dot)' : 'var(--text-muted)' }} className={i < rating ? 'fill-amber-400' : ''} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Review (optional)</label>
              <textarea
                value={review}
                onChange={e => setReview(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Share your experience..."
                className="w-full px-3 py-2.5 text-sm rounded-lg resize-none transition-all focus:outline-none focus:ring-1"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'var(--text-secondary)' }}
              />
              <p className="text-[10px] mt-1 text-right" style={{ color: 'var(--text-muted)' }}>{review.length}/500</p>
            </div>

            {/* Punch list */}
            <div>
              <label className="text-sm font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Flag punch list items (optional)</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={punchInput}
                  onChange={e => setPunchInput(e.target.value)}
                  placeholder="Add an item..."
                  className="flex-1 px-3 py-2 text-sm rounded-lg transition-all focus:outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'var(--text-secondary)' }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && punchInput.trim()) {
                      setPunchList(prev => [...prev, punchInput.trim()]);
                      setPunchInput('');
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (punchInput.trim()) {
                      setPunchList(prev => [...prev, punchInput.trim()]);
                      setPunchInput('');
                    }
                  }}
                  className="p-2 rounded-lg transition-colors"
                  style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)', border: '1px solid rgba(255,255,255,0.10)' }}
                >
                  <Plus size={14} />
                </button>
              </div>
              {punchList.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {punchList.map((item, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{item}</span>
                      <button
                        type="button"
                        onClick={() => setPunchList(prev => prev.filter((_, idx) => idx !== i))}
                        className="p-1 rounded transition-colors"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={approving || !name || rating === 0}
              className="w-full py-2.5 text-sm rounded-xl transition-all font-medium disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ background: 'var(--accent)', color: '#fff' }}
            >
              {approving ? (
                <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
              ) : (
                <CheckCircle size={15} />
              )}
              {approving ? 'Versturen...' : 'Oplevering goedkeuren'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
